import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateSoaVersionInput,
  OrgResponsibility,
  SoaChange,
  SoaVersion,
  UpdateSoaSettingsInput,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import {
  Button,
  Dialog,
  Field,
  Input,
  Select,
  Spinner,
  Table,
  Textarea,
  useToast,
} from '../../components/ui.js';
import type { MemberOption } from './SoaRow.js';
import { errorMessage, fieldErrors, issuesOf, todayIso } from './soaModel.js';

// ---------------------------------------------------------- record version

/**
 * "Record version" (admins): snapshots every control row under a label. The
 * approver defaults to the Organisation Owner responsibility when it names a
 * current member.
 */
export function RecordVersionDialog({
  open,
  onClose,
  orgId,
  versionId,
  members,
  latest,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  versionId: string;
  members: ReadonlyArray<MemberOption>;
  latest: SoaVersion | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [approvedById, setApprovedById] = useState('');
  const [approvedOn, setApprovedOn] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const approverTouched = useRef(false);

  const responsibilities = useQuery({
    queryKey: ['org-responsibilities', orgId],
    queryFn: () => apiFetch<{ items: OrgResponsibility[] }>(`/orgs/${orgId}/responsibilities`),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setLabel('');
    setReason('');
    setApprovedById('');
    setApprovedOn('');
    setErrors({});
    approverTouched.current = false;
  }, [open]);

  useEffect(() => {
    if (!open || approverTouched.current) return;
    const owner = responsibilities.data?.items.find((r) => r.roleKey === 'organisation_owner');
    if (owner?.userId && members.some((member) => member.id === owner.userId)) {
      setApprovedById(owner.userId);
    }
  }, [open, responsibilities.data, members]);

  const create = useMutation({
    mutationFn: () =>
      apiFetch<SoaVersion>(`/orgs/${orgId}/soa/${versionId}/versions`, {
        method: 'POST',
        body: {
          label: label.trim(),
          reason: reason.trim(),
          approvedById: approvedById || null,
          ...(approvedOn ? { approvedOn } : {}),
        } satisfies CreateSoaVersionInput,
      }),
    onSuccess: (version) => {
      const changes =
        version.changeCount > 0
          ? ` with ${version.changeCount} change${version.changeCount === 1 ? '' : 's'}`
          : '';
      toast(`Version ${version.label} recorded${changes}.`, 'success');
      void queryClient.invalidateQueries({ queryKey: ['soa', orgId, versionId] });
      void queryClient.invalidateQueries({ queryKey: ['soa-versions', orgId, versionId] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.code === 'conflict') {
        setErrors({ label: err.message });
        return;
      }
      const issues = issuesOf(err);
      if (issues.length > 0) {
        setErrors(fieldErrors(issues));
        return;
      }
      toast(errorMessage(err, 'Could not record the version'), 'error');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (label.trim() === '') {
      setErrors({ label: 'Enter a version label' });
      return;
    }
    setErrors({});
    create.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} title="Record a version">
      <p className="soa-dialog-intro">
        Every control row is captured as it stands now. The change log compares this version with
        the previous one, and the export is labelled with it.
      </p>
      <form onSubmit={submit} noValidate>
        <Field
          label="Version label"
          required
          error={errors.label}
          hint={latest ? `The latest recorded version is ${latest.label}` : 'e.g. 1.0'}
        >
          {(props) => (
            <Input
              {...props}
              required
              maxLength={20}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          )}
        </Field>
        <Field label="Reason for change" error={errors.reason}>
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Approved by"
          error={errors.approvedById}
          hint="Defaults to the Organisation Owner from Responsible Individuals"
        >
          {(props) => (
            <Select
              {...props}
              value={approvedById}
              onChange={(e) => {
                approverTouched.current = true;
                setApprovedById(e.target.value);
              }}
            >
              <option value="">Not yet approved</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Approved on" error={errors.approvedOn}>
          {(props) => (
            <Input
              {...props}
              type="date"
              max={todayIso()}
              value={approvedOn}
              onChange={(e) => setApprovedOn(e.target.value)}
            />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Record version
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------- edit header

/** "Edit header" (admins): the purpose paragraph and classification marking (PUT …/settings). */
export function EditHeaderDialog({
  open,
  onClose,
  orgId,
  versionId,
  purpose,
  classification,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  versionId: string;
  purpose: string;
  classification: string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [purposeText, setPurposeText] = useState(purpose);
  const [classificationText, setClassificationText] = useState(classification);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setPurposeText(purpose);
    setClassificationText(classification);
    setErrors({});
  }, [open, purpose, classification]);

  const save = useMutation({
    mutationFn: () =>
      apiFetch<UpdateSoaSettingsInput>(`/orgs/${orgId}/soa/${versionId}/settings`, {
        method: 'PUT',
        body: {
          purpose: purposeText.trim(),
          classification: classificationText.trim(),
        } satisfies UpdateSoaSettingsInput,
      }),
    onSuccess: () => {
      toast('Header saved.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['soa', orgId, versionId] });
      onClose();
    },
    onError: (err) => {
      const issues = issuesOf(err);
      if (issues.length > 0) {
        setErrors(fieldErrors(issues));
        return;
      }
      toast(errorMessage(err, 'Could not save the header'), 'error');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (classificationText.trim() === '') {
      setErrors({ classification: 'Enter a classification marking' });
      return;
    }
    setErrors({});
    save.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} title="Edit header">
      <form onSubmit={submit} noValidate>
        <Field
          label="Purpose"
          error={errors.purpose}
          hint="Printed under the title of the exported workbook, followed by the generated-on line"
        >
          {(props) => (
            <Textarea
              {...props}
              rows={6}
              maxLength={2000}
              value={purposeText}
              onChange={(e) => setPurposeText(e.target.value)}
            />
          )}
        </Field>
        <Field label="Classification" required error={errors.classification} hint="e.g. Confidential">
          {(props) => (
            <Input
              {...props}
              required
              maxLength={100}
              value={classificationText}
              onChange={(e) => setClassificationText(e.target.value)}
            />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save header
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ----------------------------------------------------------------- changes

export interface ChangesTarget {
  /** A recorded version's changes versus its predecessor; null = the working copy versus the latest version. */
  soaVersionId: string | null;
  title: string;
}

/** The change list of a version (Column = the SoA sheet heading). */
export function ChangesDialog({
  orgId,
  versionId,
  target,
  onClose,
}: {
  orgId: string;
  versionId: string;
  target: ChangesTarget | null;
  onClose: () => void;
}) {
  const open = target !== null;
  const base = `/orgs/${orgId}/soa/${versionId}`;
  const changes = useQuery({
    queryKey: ['soa-changes', orgId, versionId, target?.soaVersionId ?? 'current'],
    queryFn: () =>
      apiFetch<SoaChange[]>(
        target?.soaVersionId ? `${base}/versions/${target.soaVersionId}/changes` : `${base}/changes`,
      ),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} title={target?.title ?? 'Changes'}>
      {changes.isPending ? (
        <div className="soa-dialog-loading">
          <Spinner label="Loading changes" />
        </div>
      ) : changes.isError ? (
        <p className="soa-muted">{errorMessage(changes.error, 'Could not load the changes.')}</p>
      ) : changes.data.length === 0 ? (
        <p className="soa-muted">No cells changed.</p>
      ) : (
        <div className="soa-changes">
          <p className="soa-muted">
            {changes.data.length} changed cell{changes.data.length === 1 ? '' : 's'}
          </p>
          <Table
            head={
              <>
                <th>Control</th>
                <th>Column</th>
                <th>Previous wording</th>
                <th>New wording</th>
              </>
            }
          >
            {changes.data.map((change, index) => (
              <tr key={`${change.clauseNumber}-${change.field}-${index}`}>
                <td className="soa-num">{change.clauseNumber}</td>
                <td>{change.fieldLabel}</td>
                <td className="soa-cell-text">{change.previous || '—'}</td>
                <td className="soa-cell-text">{change.next || '—'}</td>
              </tr>
            ))}
          </Table>
        </div>
      )}
      <div className="dialog-actions">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}
